import React, {useEffect, useState} from 'react';
import {withRouter} from 'react-router-dom';
import {Col, Container, Row} from 'react-bootstrap';
import {Alert, Select, Spin, Table, Tag} from 'antd';
import moment from 'moment';
import Api from '../../../../services/Api';
import HelperGeneral from '../../../../helpers/HelperGeneral';

const {Option} = Select;

const formatDelta = (value, currencyCode) => {
    if (value === null || value === undefined || value === '') {
        return '-';
    }
    const num = Number(value);
    if (Number.isNaN(num)) {
        return '-';
    }
    const formatted = Math.abs(num).toFixed(2).replace('.', ',');
    const prefix = num > 0 ? '+' : num < 0 ? '-' : '';
    const suffix = currencyCode ? ` ${currencyCode}` : '';
    return `${prefix}${formatted}${suffix}`;
};

const txTypeColor = (txType) => {
    if (txType === 'earn') return 'green';
    if (txType === 'spend') return 'magenta';
    if (txType === 'reverse') return 'blue';
    if (txType === 'expire') return 'default';
    return 'gold';
};

const PointHistory = () => {
    const [loading, setLoading] = useState(true);
    const [summary, setSummary] = useState({});
    const [tableData, setTableData] = useState([]);
    const [pagination, setPagination] = useState({current: 1, pageSize: 15, total: 0});
    const [filters, setFilters] = useState({tx_type: undefined, source_type: undefined});
    const [loadError, setLoadError] = useState(null);

    useEffect(() => {
        getPointHistory(pagination.current, pagination.pageSize, filters);
    }, []);

    const getPointHistory = (page = pagination.current, size = pagination.pageSize, activeFilters = filters) => {
        setLoading(true);
        setLoadError(null);
        Api.getPointHistory(page, size, activeFilters).then((response) => {
            const payload = response?.data;

            if (typeof payload === 'string' || payload?.summary === undefined) {
                throw new Error('Puan geçmişi servisi geçersiz yanıt döndürdü.');
            }

            setSummary(payload.summary || {});
            setTableData(payload.transactions?.data || []);
            setPagination({
                current: payload.transactions?.meta?.current_page || page,
                pageSize: payload.transactions?.meta?.per_page || size,
                total: payload.transactions?.meta?.total || 0,
            });
            setLoading(false);
        }).catch((e) => {
            setSummary({});
            setTableData([]);
            setLoadError(e?.message || 'Puan geçmişi yüklenemedi.');
            setLoading(false);
            HelperGeneral.alert(e);
        });
    };

    const handleTableChange = (pager) => {
        setPagination(pager);
        getPointHistory(pager.current, pager.pageSize, filters);
    };

    const handleFilterChange = (key, value) => {
        const nextFilters = {...filters, [key]: value || undefined};
        setFilters(nextFilters);
        getPointHistory(1, pagination.pageSize, nextFilters);
    };

    const columns = [
        {
            title: 'Hareket Tipi',
            dataIndex: 'txTypeLabel',
            width: 120,
            render: (_, record) => (
                <Tag color={txTypeColor(record?.txType)} className={'m-0'}>
                    {record?.txTypeLabel || record?.txType || '-'}
                </Tag>
            ),
        },
        {
            title: 'Puan Değişimi',
            dataIndex: 'pointsDelta',
            width: 130,
            render: (_, record) => {
                const value = record?.pointsDelta;
                if (value === null || value === undefined) return '-';
                const num = Number(value);
                const className = num > 0 ? 'text-success' : num < 0 ? 'text-danger' : '';
                return <span className={className}>{formatDelta(value)}</span>;
            },
        },
        {
            title: 'Kalan Puan',
            dataIndex: 'remainingPoints',
            width: 110,
            render: (_, record) => {
                if (record?.remainingPoints === null || record?.remainingPoints === undefined) {
                    return '-';
                }
                return HelperGeneral.formatPoint(record.remainingPoints);
            },
        },
        {
            title: 'Parasal Etki',
            dataIndex: 'moneyDelta',
            width: 140,
            render: (_, record) => {
                const value = record?.moneyDelta;
                if (value === null || value === undefined) return '-';
                const num = Number(value);
                const className = num > 0 ? 'text-success' : num < 0 ? 'text-danger' : '';
                return (
                    <span className={className}>
                        {formatDelta(value, 'TL')}
                    </span>
                );
            },
        },
        {
            title: 'Oluşturulma',
            dataIndex: 'createdAt',
            width: 130,
            render: (_, record) => record?.createdAt
                ? moment(record.createdAt).format('DD.MM.YYYY HH:mm')
                : '-',
        },
    ];

    return (
        <Container className={'p-0'}>
            {loadError && (
                <Alert
                    type={'error'}
                    showIcon
                    message={'Puan geçmişi yüklenemedi'}
                    description={loadError}
                    className={'mb-3'}
                />
            )}
            <Spin spinning={loading}>
                <div className={'box point-history-summary'}>
                    <Row className={'g-3'}>
                        <Col xs={6} md={4}>
                            <div className={'summary-card'}>
                                <div className={'summary-label'}>Güncel Puan / TL</div>
                                <div className={'summary-value'}>{HelperGeneral.formatPoint(summary?.currentPoints ?? 0)}</div>
                            </div>
                        </Col>
                        <Col xs={6} md={4}>
                            <div className={'summary-card'}>
                                <div className={'summary-label'}>Kazanılan Puan / TL</div>
                                <div className={'summary-value text-success'}>{HelperGeneral.formatPoint(summary?.earnedPoints ?? 0)}</div>
                            </div>
                        </Col>
                        <Col xs={6} md={4}>
                            <div className={'summary-card'}>
                                <div className={'summary-label'}>Harcanan Puan / TL</div>
                                <div className={'summary-value text-danger'}>{HelperGeneral.formatPoint(summary?.paidPoints ?? 0)}</div>
                            </div>
                        </Col>
                    </Row>
                </div>

                <div className={'box product-history point-history-table'} style={{maxWidth: 'initial'}}>
                    <div className={'d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2'}>
                        <h4 className={'mb-0'}>Puan Geçmişi</h4>
                        <div className={'d-flex flex-wrap point-history-filters'}>
                            <Select
                                allowClear
                                className={'point-history-filter-select'}
                                placeholder={'Hareket tipi'}
                                style={{minWidth: 160}}
                                value={filters.tx_type}
                                onChange={(value) => handleFilterChange('tx_type', value)}
                            >
                                <Option value={'earn'}>Kazanım</Option>
                                <Option value={'spend'}>Harcama</Option>
                                <Option value={'reverse'}>İade</Option>
                                <Option value={'expire'}>Süre dolumu</Option>
                                <Option value={'adjustment'}>Düzeltme</Option>
                            </Select>
                            <Select
                                allowClear
                                className={'point-history-filter-select'}
                                placeholder={'Kaynak'}
                                style={{minWidth: 160}}
                                value={filters.source_type}
                                onChange={(value) => handleFilterChange('source_type', value)}
                            >
                                <Option value={'manual'}>Manuel</Option>
                                <Option value={'order'}>Sipariş</Option>
                                <Option value={'order_product'}>Sipariş ürünü</Option>
                                <Option value={'point_code'}>Puan kodu</Option>
                                <Option value={'campaign_user'}>Kampanya</Option>
                                <Option value={'integration'}>Entegrasyon</Option>
                                <Option value={'transfer'}>Transfer</Option>
                                <Option value={'migration'}>Migrasyon</Option>
                                <Option value={'system'}>Sistem</Option>
                            </Select>
                        </div>
                    </div>
                    <Table
                        columns={columns}
                        rowKey={(record) => record.id}
                        dataSource={tableData}
                        loading={loading}
                        scroll={{x: 1100}}
                        pagination={{
                            ...pagination,
                            showSizeChanger: true,
                            pageSizeOptions: ['15', '30', '45', '60', '100'],
                            responsive: false,
                        }}
                        onChange={handleTableChange}
                    />
                </div>
            </Spin>
        </Container>
    );
};

export default withRouter(PointHistory);
